import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Icon, Tooltip } from '@mui/material';
import cx from 'classnames';
import { NotificationManager } from 'Components/Notifications';
import { revealIntegrationApiKey } from '../../redux/slice';
import './styles.scss';

const COPIED_MS = 1500;

/**
 * Copy button of the API Key column (design B): the grid only has the masked key, so the click asks px-cloud for the
 * decrypted one (`revealCompanyIntegrationApiKey`, audited) and writes it to the clipboard without keeping it in the
 * store. The icon turns into a check and the tooltip into "Copied" for a moment.
 */
const CopyApiKey = ({ intl: { messages }, integration }) => {
    const [state, setState] = useState('idle');
    const timer = useRef();
    useEffect(() => () => clearTimeout(timer.current), []);

    const copy = async (event) => {
        event.stopPropagation();
        if (state === 'copying') return;
        setState('copying');
        try {
            await writeToClipboard(revealIntegrationApiKey(integration.id));
            setState('copied');
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setState('idle'), COPIED_MS);
        } catch (e) {
            setState('idle');
            NotificationManager.error(messages['accountApps_copyError'], messages['error'], 3000, null, null, 'warning');
        }
    };

    const copied = state === 'copied';
    const label = messages[copied ? 'accountApps_copied' : 'accountApps_copyKey'];
    return (
        <Tooltip title={label} placement="top">
            <button type="button" className={cx('account-apps-copy', { 'is-copied': copied })} aria-label={messages['accountApps_copyKey']} aria-busy={state === 'copying'} onClick={copy}>
                <Icon>{copied ? 'check' : 'content_copy'}</Icon>
            </button>
        </Tooltip>
    );
};

/**
 * Writes the text a promise resolves to. A ClipboardItem built from the promise keeps the click's user activation
 * while the key is fetched (Safari requires it); browsers without it write the text once it arrives.
 */
export const writeToClipboard = async (textPromise) => {
    const { clipboard } = navigator;
    if (typeof window.ClipboardItem === 'function' && clipboard?.write) {
        try {
            const blob = textPromise.then((text) => new Blob([text], { type: 'text/plain' }));
            // A failed reveal is reported through textPromise below; the clipboard may never read this one
            blob.catch(() => {});
            await clipboard.write([new window.ClipboardItem({ 'text/plain': blob })]);
            return;
        } catch (e) {
            // A failed reveal fails both ways; otherwise fall back to writeText
            await textPromise;
        }
    }
    await clipboard.writeText(await textPromise);
};

export default injectIntl(CopyApiKey);
